import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../services/api';

interface Admin {
  id: number;
  name: string;
  email: string;
  role: 'super_admin' | 'admin';
}

interface AdminAuthContextType {
  admin: Admin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshAdmin: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

interface AdminAuthProviderProps {
  children: React.ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Verificar se o token é válido
      const response = await api.adminMe();

      if (response.success) {
        setAdmin(response.data);
      } else {
        localStorage.removeItem('admin_token');
      }
    } catch (error) {
      console.error('Error checking admin auth status:', error);
      localStorage.removeItem('admin_token');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAdmin = async () => {
    try {
      const response = await api.adminMe();
      if (response.success) {
        setAdmin(response.data);
      }
    } catch (error) {
      console.error('Error refreshing admin:', error);
    }
  };

  const login = async (credentials: { email: string; password: string }) => {
    try {
      const response = await api.adminLogin(credentials);

      if (response.success) {
        const { token, admin: adminData } = response.data;

        // Salvar token
        localStorage.setItem('admin_token', token);

        // Atualizar estado
        setAdmin(adminData);
      } else {
        throw new Error(response.message || 'Erro no login');
      }
    } catch (error: any) {
      console.error('Admin login error:', error);

      if (error.response?.status === 401) {
        throw new Error('Credenciais de administrador inválidas');
      } else if (error.response?.status === 403) {
        throw new Error('Acesso negado. Apenas administradores podem acessar');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Erro interno do servidor');
      }
    }
  };

  const logout = async () => {
    try {
      await api.adminLogout();
    } catch (error) {
      console.error('Admin logout error:', error);
    } finally {
      // Limpar dados locais
      localStorage.removeItem('admin_token');
      setAdmin(null);
    }
  };

  const contextValue: AdminAuthContextType = {
    admin,
    isAuthenticated: !!admin,
    isLoading,
    login,
    logout,
    refreshAdmin,
  };

  return (
    <AdminAuthContext.Provider value={contextValue}>
      {children}
    </AdminAuthContext.Provider>
  );
};
